import type {
  Diagram,
  DiagramReviewCounts,
  NoteType,
  ReviewComment,
  ThreatCatNote,
  ThreadedReviewComment,
} from '@/types';

export type { DiagramReviewCounts, ThreadedReviewComment };

export interface ReviewCommentsIndexInput {
  reviewComments?: ReviewComment[];
  diagrams?: Diagram[];
  notes?: ThreatCatNote[];
}

export interface ReviewCommentsIndex {
  threadedRootComments: ThreadedReviewComment[];
  projectVersion: ThreadedReviewComment[];
  getDiagramCounts: (diagramId: number | null | undefined) => DiagramReviewCounts;
  getDiagramComments: (diagramId: number) => ThreadedReviewComment[];
  getEntityComments: (entityId: number, diagramId?: number | null) => ThreadedReviewComment[];
  getPathComments: (pathId: number, diagramId?: number | null) => ThreadedReviewComment[];
  getBoundaryComments: (boundaryId: number, diagramId?: number | null) => ThreadedReviewComment[];
  getThreatComments: (threatId: number) => ThreadedReviewComment[];
  getNoteComments: (diagramId: number | null, noteType: NoteType) => ThreadedReviewComment[];
  getNoteCommentsById: (noteId: number) => ThreadedReviewComment[];
}

const ZERO_COUNTS: DiagramReviewCounts = Object.freeze({
  total: 0,
  overview: 0,
  connections: 0,
  entities: 0,
  boundaries: 0,
  notes: 0,
});

export function buildReviewCommentsIndex({
  reviewComments = [],
  diagrams = [],
  notes = [],
}: ReviewCommentsIndexInput): ReviewCommentsIndex {
  const threadedRootComments: ThreadedReviewComment[] = [];
  const rootById = new Map<number, ThreadedReviewComment>();
  const replies: ReviewComment[] = [];

  for (const c of reviewComments) {
    if (c.target_type === 'comment') {
      replies.push(c);
      continue;
    }
    const threaded: ThreadedReviewComment = {
      ...c,
      replies: [],
    };
    threadedRootComments.push(threaded);
    rootById.set(c.id, threaded);
  }

  const replyParentMap = new Map<number, number>();
  for (const r of replies) {
    replyParentMap.set(r.id, r.target_id);
  }

  const findRootId = (cid: number | null | undefined): number | null => {
    const visited = new Set<number>();
    let curr = cid;
    while (typeof curr === 'number' && !visited.has(curr)) {
      if (rootById.has(curr)) return curr;
      visited.add(curr);
      curr = replyParentMap.get(curr);
    }
    return null;
  };

  for (const r of replies) {
    const rootId = findRootId(r.target_id) ?? findRootId(r.parent_id);
    if (rootId !== null && rootById.has(rootId)) {
      rootById.get(rootId)!.replies.push(r);
    }
  }

  const notesById = new Map<number, { type: NoteType; diagramId: number | null }>();
  for (const n of notes) {
    if (typeof n.id === 'number') {
      notesById.set(n.id, { type: n.type, diagramId: n.diagram_id ?? null });
    }
  }

  const entityDiagramMap = new Map<number, number>();
  const pathDiagramMap = new Map<number, number>();
  const boundaryDiagramMap = new Map<number, number>();
  const countsByDiagramId = new Map<number, DiagramReviewCounts>();

  for (const d of diagrams) {
    if (typeof d.id !== 'number') continue;
    countsByDiagramId.set(d.id, {
      total: 0,
      overview: 0,
      connections: 0,
      entities: 0,
      boundaries: 0,
      notes: 0,
    });
    for (const e of d.entities ?? []) {
      entityDiagramMap.set(e.id, d.id);
    }
    for (const p of d.paths ?? []) {
      pathDiagramMap.set(p.id, d.id);
    }
    for (const b of d.boundaries ?? []) {
      boundaryDiagramMap.set(b.id, d.id);
    }
  }

  const projectVersion: ThreadedReviewComment[] = [];
  const diagramComments = new Map<number, ThreadedReviewComment[]>();
  const entityComments = new Map<number, ThreadedReviewComment[]>();
  const pathComments = new Map<number, ThreadedReviewComment[]>();
  const boundaryComments = new Map<number, ThreadedReviewComment[]>();
  const threatComments = new Map<number, ThreadedReviewComment[]>();
  const noteComments = new Map<string, ThreadedReviewComment[]>();
  const noteCommentsById = new Map<number, ThreadedReviewComment[]>();

  const pushMap = <K>(map: Map<K, ThreadedReviewComment[]>, key: K, comment: ThreadedReviewComment) => {
    const list = map.get(key);
    if (list) {
      list.push(comment);
    } else {
      map.set(key, [comment]);
    }
  };

  const indexDiagramElementComment = (
    comment: ThreadedReviewComment,
    targetMap: Map<number, ThreadedReviewComment[]>,
    elementDiagramMap: Map<number, number>,
    countKey: 'entities' | 'connections' | 'boundaries',
  ) => {
    pushMap(targetMap, comment.target_id, comment);
    const diagId =
      comment.parent_type === 'diagram' && typeof comment.parent_id === 'number'
        ? comment.parent_id
        : elementDiagramMap.get(comment.target_id);
    if (typeof diagId === 'number') {
      const counts = countsByDiagramId.get(diagId);
      if (counts) {
        counts[countKey] += 1;
        counts.total += 1;
      }
    }
  };

  for (const comment of threadedRootComments) {
    if (comment.status !== 'suggested') continue;

    if (comment.target_type === 'project_version') {
      projectVersion.push(comment);
    } else if (comment.target_type === 'diagram') {
      pushMap(diagramComments, comment.target_id, comment);
      const counts = countsByDiagramId.get(comment.target_id);
      if (counts) {
        counts.overview += 1;
        counts.total += 1;
      }
    } else if (comment.target_type === 'entity') {
      indexDiagramElementComment(comment, entityComments, entityDiagramMap, 'entities');
    } else if (comment.target_type === 'path') {
      indexDiagramElementComment(comment, pathComments, pathDiagramMap, 'connections');
    } else if (comment.target_type === 'boundary') {
      indexDiagramElementComment(comment, boundaryComments, boundaryDiagramMap, 'boundaries');
    } else if (comment.target_type === 'threat') {
      pushMap(threatComments, comment.target_id, comment);
    } else if (comment.target_type === 'note') {
      pushMap(noteCommentsById, comment.target_id, comment);
      const noteInfo = notesById.get(comment.target_id);
      const diagId =
        noteInfo !== undefined
          ? noteInfo.diagramId
          : comment.parent_type === 'diagram' && typeof comment.parent_id === 'number'
            ? comment.parent_id
            : null;
      if (noteInfo) {
        pushMap(noteComments, `${diagId ?? 'project'}:${noteInfo.type}`, comment);
      }
      if (typeof diagId === 'number') {
        const counts = countsByDiagramId.get(diagId);
        if (counts) {
          counts.notes += 1;
          counts.total += 1;
        }
      }
    }
  }

  const filterByDiagram = (
    list: ThreadedReviewComment[],
    elementDiagramMap: Map<number, number>,
    diagramId?: number | null,
  ) => {
    if (typeof diagramId !== 'number') return list;
    return list.filter((c) => {
      const resolvedDiagramId =
        c.parent_type === 'diagram' && typeof c.parent_id === 'number'
          ? c.parent_id
          : elementDiagramMap.get(c.target_id);
      return resolvedDiagramId === diagramId;
    });
  };

  return {
    threadedRootComments,
    projectVersion,
    getDiagramCounts: (diagramId) => {
      if (typeof diagramId !== 'number') return ZERO_COUNTS;
      return countsByDiagramId.get(diagramId) ?? ZERO_COUNTS;
    },
    getDiagramComments: (diagramId) => diagramComments.get(diagramId) ?? [],
    getEntityComments: (entityId, diagramId) =>
      filterByDiagram(entityComments.get(entityId) ?? [], entityDiagramMap, diagramId),
    getPathComments: (pathId, diagramId) =>
      filterByDiagram(pathComments.get(pathId) ?? [], pathDiagramMap, diagramId),
    getBoundaryComments: (boundaryId, diagramId) =>
      filterByDiagram(boundaryComments.get(boundaryId) ?? [], boundaryDiagramMap, diagramId),
    getThreatComments: (threatId) => threatComments.get(threatId) ?? [],
    getNoteComments: (diagramId, noteType) =>
      noteComments.get(`${diagramId ?? 'project'}:${noteType}`) ?? [],
    getNoteCommentsById: (noteId) => noteCommentsById.get(noteId) ?? [],
  };
}
